/*
Horizontal photo gallery — used by the map's arbre detail sheet to
show all captures of a single arbre. Mirrors Android's PhotoGallery
(120 dp thumbs, LazyRow, "Tes photos (N)" title).

Each thumbnail wires:
- click → onThumbClick(index) — typically opens the lightbox
- long-press (≥ 500 ms) → onThumbLongPress(index) — typically opens
  the delete confirmation dialog

The component owns its DOM and an array of object URLs (revoked on
destroy so a remount of the sheet doesn't leak Blobs).
*/

const kLongPressMs: number = 500  // wall-clock threshold for a long-press; matches Compose's awaitLongPressOrCancellation default

export interface PhotoGalleryItem
{
	captureId: number  // primary key in the captures store — used by deletion handlers
	url: string  // object URL of the photo Blob; lifetime is owned by this component
	caption?: string
}

export interface PhotoGalleryArgs
{
	host: HTMLElement
	title: string  // header text, e.g. "Tes photos (3)"
	items: PhotoGalleryItem[]
	onThumbClick: (inIndex: number) => void
	onThumbLongPress: (inIndex: number) => void
}

export interface PhotoGalleryController
{
	destroy(): void
}

export function mountPhotoGallery(inArgs: PhotoGalleryArgs): PhotoGalleryController
{
	const vHost = inArgs.host
	vHost.innerHTML = `
		<div class="photo-gallery">
			<h3 class="photo-gallery-title">${escapeHtml(inArgs.title)}</h3>
			<div class="photo-gallery-row" role="list"></div>
		</div>
	`
	const vRow = vHost.querySelector<HTMLElement>('.photo-gallery-row')
	if (!vRow) return { destroy(): void { vHost.innerHTML = '' } }

	const vCleanups: Array<() => void> = []  // touch/click listener removers, ran on destroy

	inArgs.items.forEach((inItem, inIndex) => {
		const vBtn = document.createElement('button')
		vBtn.className = 'photo-gallery-thumb'
		vBtn.setAttribute('role', 'listitem')
		vBtn.dataset.captureId = String(inItem.captureId)

		const vImg = document.createElement('img')
		vImg.alt = inItem.caption ?? ''
		vImg.src = inItem.url
		vImg.loading = 'lazy'
		vBtn.appendChild(vImg)
		vRow.appendChild(vBtn)

		// Long-press timer + a "consumed" flag so we don't fire the
		// click handler on release once the long-press has already fired.
		let vTimer: number | null = null
		let vConsumed: boolean = false

		const vStart = (): void => {
			vConsumed = false
			if (vTimer !== null) window.clearTimeout(vTimer)
			vTimer = window.setTimeout(() => {
				vTimer = null
				vConsumed = true
				inArgs.onThumbLongPress(inIndex)
			}, kLongPressMs)
		}
		const vCancel = (): void => {
			if (vTimer !== null) { window.clearTimeout(vTimer); vTimer = null }
		}
		const vClick = (inEv: MouseEvent | TouchEvent): void => {
			if (vConsumed) { inEv.preventDefault(); return }
			inArgs.onThumbClick(inIndex)
		}

		vBtn.addEventListener('touchstart', vStart, { passive: true })
		vBtn.addEventListener('touchend', vCancel)
		vBtn.addEventListener('touchmove', vCancel)
		vBtn.addEventListener('touchcancel', vCancel)
		vBtn.addEventListener('mousedown', vStart)
		vBtn.addEventListener('mouseup', vCancel)
		vBtn.addEventListener('mouseleave', vCancel)
		vBtn.addEventListener('click', vClick)

		vCleanups.push(() => {
			vCancel()
			vBtn.removeEventListener('touchstart', vStart)
			vBtn.removeEventListener('touchend', vCancel)
			vBtn.removeEventListener('touchmove', vCancel)
			vBtn.removeEventListener('touchcancel', vCancel)
			vBtn.removeEventListener('mousedown', vStart)
			vBtn.removeEventListener('mouseup', vCancel)
			vBtn.removeEventListener('mouseleave', vCancel)
			vBtn.removeEventListener('click', vClick)
		})
	})

	return {
		destroy(): void
		{
			for (const vCleanup of vCleanups) vCleanup()
			vHost.innerHTML = ''
		},
	}
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
